import { DestroyRef, inject, Injectable } from '@angular/core';
import { take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TerminalParameterKeys } from '../../../terminals/core/enums/TerminalParameterKeys';
import { TerminalsStore } from '../../../terminals/core/stores/TerminalsStore';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';

@Injectable({
  providedIn: 'root'
})
export class WorkerRequirementService {

  private readonly _terminalsStore = inject(TerminalsStore);
  private _promptedSessionId: number | null = null;

  public isWorkerRequired(): boolean {
    const useWorker = this._terminalsStore.terminalParametersMap()[TerminalParameterKeys.CSH_USE_WORKER];
    if (useWorker === false) {
      return false;
    }

    const sessionId = this.currentSessionId();
    if (sessionId == null) {
      return true;
    }

    return sessionStorage.getItem(this.optOutKey(sessionId)) !== '1';
  }

  public continueWithoutWorker(): void {
    const sessionId = this.currentSessionId();
    if (sessionId == null) {
      return;
    }

    sessionStorage.setItem(this.optOutKey(sessionId), '1');
  }

  public confirmContinueWithoutWorker(destroyRef: DestroyRef): Promise<void> {
    if (!this.isWorkerRequired() || this._promptedSessionId === this.currentSessionId()) {
      return Promise.resolve();
    }

    this._promptedSessionId = this.currentSessionId();

    return new Promise(resolve => {
      ConfirmationModalComponent.instance.onCancel
        .pipe(take(1), takeUntilDestroyed(destroyRef))
        .subscribe(() => resolve());

      ConfirmationModalComponent.openConfirmationModal(
        'Worker'.trans('worker/session-prompt.Title'),
        'A worker is not connected. Do you want to continue without a worker and suppress the errors?'.trans('worker/session-prompt.Text'),
        destroyRef,
        () => {
          this.continueWithoutWorker();
          resolve();
        }
      );
    });
  }

  private currentSessionId(): number | null {
    return this._terminalsStore.selectedTerminal()?.currentSession?.id ?? null;
  }

  private optOutKey(sessionId: number): string {
    return `webcash:skip-worker:${sessionId}`;
  }
}
